import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { useToast } from '@/shared/ui/Toast';
import { communityApi, type PlanTouchInput } from '../api/communityApi';
import { communityKeys } from '../queryKeys';

export function useCommunityLocationId(): string {
  const { currentLocation } = useLocation();
  return currentLocation?.id ?? '';
}

export function useCommunityState() {
  const locationId = useCommunityLocationId();
  return useQuery({
    queryKey: communityKeys.state(locationId),
    queryFn: () => communityApi.getState(locationId),
    enabled: !!locationId,
    staleTime: 60_000,
  });
}

export function useCommunityPicks() {
  const locationId = useCommunityLocationId();
  return useQuery({
    queryKey: communityKeys.picks(locationId),
    queryFn: () => communityApi.getPicks(locationId),
    enabled: !!locationId,
    // While a scan runs or Joe is writing, check back so the picks appear without a reload. A scan
    // takes a minute or two, so it is checked often; Joe's writing can take longer.
    refetchInterval: (query) => {
      const status = query.state.data?.lastScan?.status;
      if (status === 'running') return 5_000;
      return status === 'awaiting_joe' ? 20_000 : false;
    },
  });
}

export function useCommunityNetwork() {
  const locationId = useCommunityLocationId();
  return useQuery({
    queryKey: communityKeys.network(locationId),
    queryFn: () => communityApi.getNetwork(locationId),
    enabled: !!locationId,
  });
}

export function usePartner(partnerId: string | null) {
  const locationId = useCommunityLocationId();
  return useQuery({
    queryKey: communityKeys.partner(locationId, partnerId ?? ''),
    queryFn: () => communityApi.getPartner(locationId, partnerId!),
    enabled: !!locationId && !!partnerId,
  });
}

/** Every write invalidates the three reads the page composes from. */
export function useCommunityActions() {
  const locationId = useCommunityLocationId();
  const queryClient = useQueryClient();
  const toast = useToast();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: communityKeys.picks(locationId) });
    queryClient.invalidateQueries({ queryKey: communityKeys.network(locationId) });
    queryClient.invalidateQueries({ queryKey: communityKeys.partners(locationId) });
  };
  // Every write surfaces a failed request (403 for a viewer, 400 for a stale action, a network
  // drop) as a toast with the server's message, so a click never silently does nothing.
  const failed = (fallback: string) => (err: Error) => toast.error(err.message || fallback);
  const snooze = useMutation({ mutationFn: (pickId: string) => communityApi.snoozePick(locationId, pickId), onSuccess: invalidate, onError: failed('Could not snooze the pick') });
  // The scan runs in the background; refetching shows "Joe is scanning now" and the picks poll on.
  const refresh = useMutation({ mutationFn: () => communityApi.refreshPicks(locationId), onSuccess: invalidate, onError: failed('Could not start a new scan') });
  const addReserve = useMutation({ mutationFn: (pickId: string) => communityApi.addReservePick(locationId, pickId), onSuccess: invalidate, onError: failed('Could not add the pick') });
  const dismiss = useMutation({
    mutationFn: ({ pickId, reason }: { pickId: string; reason: string }) => communityApi.dismissPick(locationId, pickId, reason),
    onSuccess: invalidate,
    onError: failed('Could not pass on the pick'),
  });
  const undo = useMutation({ mutationFn: (ruleId: string) => communityApi.undoRule(locationId, ruleId), onSuccess: invalidate, onError: failed('Could not undo that') });
  const plan = useMutation({ mutationFn: (input: PlanTouchInput) => communityApi.planTouch(locationId, input), onSuccess: invalidate, onError: failed('Could not save the touch') });
  const touchStatus = useMutation({
    mutationFn: ({ touchId, status, date }: { touchId: string; status: 'done' | 'skipped'; date?: string }) => communityApi.setTouchStatus(locationId, touchId, status, date),
    onSuccess: invalidate,
    onError: failed('Could not update the touch'),
  });
  const logTouch = useMutation({
    mutationFn: ({ partnerId, ...input }: { partnerId: string; activityType: string; activityOther?: string | null; date: string; notes?: string | null }) =>
      communityApi.logTouch(locationId, partnerId, input),
    onSuccess: invalidate,
    onError: failed('Could not log the touch'),
  });
  const updatePartner = useMutation({
    mutationFn: ({ partnerId, ...patch }: { partnerId: string; rhythm?: string; rhythmDays?: number | null; archived?: boolean }) =>
      communityApi.updatePartner(locationId, partnerId, patch),
    onSuccess: invalidate,
    onError: failed('Could not update the partner'),
  });
  const settings = useMutation({
    mutationFn: (patch: { radiusMi?: number; enabledCategories?: string[]; enabledEventTypes?: string[]; referralCheckinOn?: boolean }) => communityApi.updateSettings(locationId, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.state(locationId) });
      invalidate();
    },
    onError: failed('Could not save the settings'),
  });
  const addEvent = useMutation({
    mutationFn: (input: { title: string; start: string; org?: string | null; url?: string | null }) => communityApi.addEvent(locationId, input),
    onSuccess: invalidate,
    onError: failed('Could not add the event'),
  });
  return { locationId, snooze, dismiss, undo, plan, touchStatus, logTouch, updatePartner, settings, addEvent, refresh, addReserve };
}
